import React, { useMemo, useState } from "react";

// region External libraries
import { Button } from "@libraries/mui/components";
// endregion External libraries
// region Atoms
import SearchField from "@atoms/SearchField";
// endregion Atoms
// region Languages
import useTranslation from "@languages/useTranslation";
// endregion Languages

import { LegacyMigrationMessages } from "../languages/interfaces";
import { LegacyMigrationTenant } from "../service";
import * as Styled from "../styles";

interface IChooseCustomerProps {
  tenants: LegacyMigrationTenant[];
  /** False while the list has never been read; see the empty state below. */
  loaded: boolean;
  busy: boolean;
  configured: boolean;
  onStart: (tenantId: string) => void;
}

/**
 * Step 1 — which customer to migrate. Every customer is listed: running one
 * again is safe, and the review shows who is already in Identity.
 */
const ChooseCustomer: React.FC<IChooseCustomerProps> = ({ tenants, loaded, busy, configured, onStart }) => {

  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  const [search, setSearch] = useState<string>("");
  // endregion States

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return tenants;

    return tenants.filter((tenant) => tenant.name.toLowerCase().includes(term)
      || tenant.alias.toLowerCase().includes(term));
  }, [tenants, search]);

  return (
    <Styled.Card>
      <Styled.StepTitle>{t(LegacyMigrationMessages.legacyMigrationChooseTitle)}</Styled.StepTitle>
      {loaded && (
        <Styled.StepHint>
          {t(LegacyMigrationMessages.legacyMigrationChooseHint, { count: tenants.length })}
        </Styled.StepHint>
      )}

      {tenants.length > 6 && (
        <Styled.Toolbar>
          <Styled.SearchBox>
            <SearchField
              fieldLabel={t(LegacyMigrationMessages.legacyMigrationSearchTenant)}
              onSearchClick={setSearch}
              onClearClick={() => setSearch("")}
            />
          </Styled.SearchBox>
        </Styled.Toolbar>
      )}

      {/* An empty list because the request failed must not read as "no
          customers": the banner above says what went wrong. */}
      {visible.length === 0 && (
        <Styled.Empty>
          {t(loaded
            ? LegacyMigrationMessages.legacyMigrationNoTenants
            : LegacyMigrationMessages.legacyMigrationTenantsUnavailable)}
        </Styled.Empty>
      )}

      <Styled.TenantList>
        {visible.map((tenant) => (
          <Styled.TenantRow key={tenant.id}>
            <Styled.TenantName>
              <strong>
                {tenant.name}
                {!tenant.active && ` · ${t(LegacyMigrationMessages.legacyMigrationInactiveTenant)}`}
              </strong>
              <span>{tenant.alias}</span>
            </Styled.TenantName>

            {/* One per row, so it stays light: the list is the choice. */}
            <Button variant="outlined" disabled={busy || !configured} onClick={() => onStart(tenant.id)}>
              {t(LegacyMigrationMessages.legacyMigrationStart)}
            </Button>
          </Styled.TenantRow>
        ))}
      </Styled.TenantList>
    </Styled.Card>
  );
};

export default ChooseCustomer;
